import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Edit3,
    Eye,
    Loader2,
    Palette,
    Rocket,
    Save,
    Trash2,
} from "lucide-react";
import { PartnerAPI } from "@/lib/partnerApi";
import { formatApiError } from "@/context/PartnerAuthContext";

const FIELDS = [
    { key: "store_name", label: "Store name" },
    { key: "tagline", label: "Tagline" },
    { key: "hero_headline", label: "Hero headline" },
    { key: "hero_subcopy", label: "Hero subcopy", textarea: true },
    { key: "footer_line", label: "Footer line" },
    { key: "accent_color", label: "Accent color (hex)", type: "color" },
];

export default function BrandingEditor() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);

    const load = async () => {
        try {
            const d = await PartnerAPI.branding(true);
            setData(d);
            const base = {};
            FIELDS.forEach((f) => {
                base[f.key] = d[f.key] ?? "";
            });
            setForm(base);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };
    useEffect(() => {
        load();
    }, []);

    if (!data) {
        return (
            <div className="py-10 text-center">
                <Loader2 size={24} className="animate-spin inline text-gold" />
            </div>
        );
    }

    const saveDraft = async () => {
        setSaving(true);
        try {
            await PartnerAPI.saveBrandingDraft(form);
            toast.success("Branding draft saved · not yet live");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const publish = async () => {
        setPublishing(true);
        try {
            await PartnerAPI.publishBranding();
            toast.success("Branding published · 48h revert window open");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setPublishing(false);
        }
    };

    const discard = async () => {
        try {
            await PartnerAPI.discardBrandingDraft();
            toast.success("Draft discarded");
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        }
    };

    const hasDraft = data._has_draft;

    return (
        <section data-testid="partner-branding-editor" className="space-y-6">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <span className="divider-rule text-gold">
                        <Palette size={12} /> Branding Editor
                    </span>
                    <h2 className="font-serif text-3xl text-cream mt-5">
                        Your Store Identity
                    </h2>
                    <p className="text-cream/60 text-sm mt-1 max-w-2xl">
                        Edit copy and colors. Drafts never go live until
                        you click Publish — then the 48-hour revert window
                        opens.
                    </p>
                </div>
                <a
                    href="/"
                    target="_blank"
                    rel="noreferrer"
                    className="btn-pill btn-ghost-light text-xs"
                >
                    <Eye size={13} /> View live site
                </a>
            </header>

            {hasDraft && (
                <div
                    className="p-4 bg-gold/15 border border-gold rounded-sm text-sm text-cream flex items-center gap-3 flex-wrap"
                    data-testid="partner-branding-draft-badge"
                >
                    <Edit3 size={14} className="text-gold" />
                    Draft pending publish ·{" "}
                    {Object.keys(data._draft || {}).length} field
                    {Object.keys(data._draft || {}).length === 1 ? "" : "s"}{" "}
                    changed
                </div>
            )}

            <div className="grid gap-4">
                {FIELDS.map((f) => (
                    <label
                        key={f.key}
                        className="block"
                        data-testid={`partner-branding-field-${f.key}`}
                    >
                        <span className="text-[0.6rem] tracking-[0.28em] uppercase text-cream/60">
                            {f.label}
                        </span>
                        {f.textarea ? (
                            <textarea
                                rows={3}
                                value={form[f.key] || ""}
                                onChange={(e) =>
                                    setForm({ ...form, [f.key]: e.target.value })
                                }
                                className="mt-1.5 w-full px-4 py-3 bg-cream/5 border border-cream/15 rounded-sm text-cream focus:outline-none focus:border-gold"
                            />
                        ) : f.type === "color" ? (
                            <div className="mt-1.5 flex gap-2 items-center">
                                <input
                                    type="color"
                                    value={form[f.key] || "#D4993D"}
                                    onChange={(e) =>
                                        setForm({ ...form, [f.key]: e.target.value })
                                    }
                                    className="h-10 w-16 border border-cream/15 rounded-sm bg-cream/5"
                                />
                                <input
                                    type="text"
                                    value={form[f.key] || ""}
                                    onChange={(e) =>
                                        setForm({ ...form, [f.key]: e.target.value })
                                    }
                                    className="flex-1 px-4 py-2.5 bg-cream/5 border border-cream/15 rounded-sm text-cream focus:outline-none focus:border-gold font-mono text-sm"
                                />
                            </div>
                        ) : (
                            <input
                                type="text"
                                value={form[f.key] || ""}
                                onChange={(e) =>
                                    setForm({ ...form, [f.key]: e.target.value })
                                }
                                className="mt-1.5 w-full px-4 py-3 bg-cream/5 border border-cream/15 rounded-sm text-cream focus:outline-none focus:border-gold"
                            />
                        )}
                    </label>
                ))}
            </div>

            <div className="flex gap-3 flex-wrap pt-3">
                <button
                    type="button"
                    onClick={saveDraft}
                    disabled={saving}
                    data-testid="partner-branding-save-draft"
                    className="btn-pill btn-primary"
                >
                    {saving ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : (
                        <>
                            <Save size={14} /> Save draft
                        </>
                    )}
                </button>
                {hasDraft && (
                    <>
                        <button
                            type="button"
                            onClick={publish}
                            disabled={publishing}
                            data-testid="partner-branding-publish"
                            className="btn-pill btn-gold"
                        >
                            {publishing ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <>
                                    <Rocket size={14} /> Publish
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={discard}
                            data-testid="partner-branding-discard"
                            className="btn-pill btn-ghost-light text-xs"
                        >
                            <Trash2 size={13} /> Discard draft
                        </button>
                    </>
                )}
            </div>
        </section>
    );
}
